import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArcherContainer } from "react-archer";
import { useProductRule } from "./hooks/use-productRuleData";
import { useForeignShopRule } from "./hooks/use-foreignShopRuleData";
import { useActionRule } from "./hooks/use-actionRuleDate";
import { useOptions } from "./hooks/use-optionsData";
import Select from "react-select"
import ProductRuleCard from "./components/PPPriceManagementProductRuleCard";
import ForeignShopRuleCard from "./components/PPPriceManagementForeignShopRuleCard";
import ActionRuleCard from "./components/PPPriceManagementActionRuleCard";
import PPPriceManagementProductRuleModal from "./modals/PPPriceManagementProductRuleModal";
import PPPriceManagementForeignShopRuleModal from "./modals/PPPriceManagementForeignShopRuleModal";
import PPPriceManagementActionRuleModal from "./modals/PPPriceManagementActionRuleModal";
import PPUtils from "../../utils/PPUtils";
import PPMatchingStatusBanner from "../../components/matching/PPMatchingStatusBanner";
import PPAddRuleGroupModal from "./modals/PPAddRuleGroupModal";
import PPBackupRuleSelectionModal from "./modals/PPBackupRuleSelectionModal";
import PPRuleTemplateSelectionModal from "./modals/PPRuleTemplateSelectionModal";
import PPPriceManagementTestRuleModal from "./modals/PPPriceManagementTestRuleModal";
import PPPriceManagementRuleHistoryModal from "./modals/PPPriceManagementRuleHistoryModal";
import PPPrioritySelectionModal from "./modals/PPPrioritySelectionModal";
import PPDeleteConfirmationModal from "./modals/PPDeleteConfirmationModal";
import PPConstraintViolationModal from "./modals/PPConstraintViolationModal";
import RuleEngineBackend from "../services/RuleEngineBackend";
import RuleStateService from "../services/RuleStateService";
import RuleConstraintService from "../services/RuleConstraintService";
import { findChannelById, resolveChannelId, setStoredChannelId } from "../common/priceManagementChannelStorage";
import "../../styles/priceman.css"

const BACKUP_RULE_GROUP_NAME = "Backup Regeln";

export default function PPPriceManagementEditor() {

     const [properties, setProperties] = useState({
          name: "",
          description: "",
          priority: 0,
          group: null,
          channel: null,
          factfinder: false,
          sepcialRule: false,
          triggerRule: false,
          shippingCost: false,
          backupRule: null
     });

     const [ruleGroups, setRuleGroups] = useState([]);
     const [channels, setChannels] = useState([]);
     const [isLoading, setIsLoading] = useState(false);
     const [isSaving, setIsSaving] = useState(false);

     let auth = PPUtils.getLogin()
     const userId = auth.user?.id
     const token = auth.token
     const customerId = PPUtils.getEffectiveCustomerId(auth)

     const { ruleId } = useParams();
     const navigate = useNavigate();
     const location = useLocation();
     const searchParams = new URLSearchParams(location.search);
     const isBackupRuleMode =
          searchParams.get("mode") === "backup" ||
          searchParams.get("createBackupRule") === "true";
     const templateRuleIdParam = searchParams.get("templateRuleId");

     const productRuleHelpers = useProductRule();
     const { ProductRuleHelper, LanguageAndChannelHelper } = productRuleHelpers;

     const foreignShopRuleHelpers = useForeignShopRule();
     const { ForeignShopRuleHelper } = foreignShopRuleHelpers;

     const actionRuleHelpers = useActionRule();
     const { ActionRuleHelper, PriceChangeMarginHelper } = actionRuleHelpers;

     const { fetchServerOptions, computeOptions } = useOptions();

     const [productRuleModalVisible, setProductRuleModalVisible] = useState(false);
     const [foreignShopRuleModalVisible, setForeignShopRuleModalVisible] = useState(false);
     const [actionRuleModalVisible, setActionRuleModalVisible] = useState(false);
     const [addGroupModalVisible, setAddGroupModalVisible] = useState(false);
     const [backupRuleModalVisible, setBackupRuleModalVisible] = useState(false);
     const [templateModalVisible, setTemplateModalVisible] = useState(false);
     const [templateRuleInfo, setTemplateRuleInfo] = useState(null);
     const [testRuleModalVisible, setTestRuleModalVisible] = useState(false);
     const [historyModalVisible, setHistoryModalVisible] = useState(false);
     const [priorityModalVisible, setPriorityModalVisible] = useState(false);
     const [editingRule, setEditingRule] = useState(null);

     const [deleteModalVisible, setDeleteModalVisible] = useState(false);
     const [ruleToDelete, setRuleToDelete] = useState(null);
     const [deleteModalConfig, setDeleteModalConfig] = useState({
          title: "Regel löschen",
          message: "Sind Sie sicher, dass Sie diese Regel löschen möchten?",
          additionalMessage: null
     });

     const [constraintModalVisible, setConstraintModalVisible] = useState(false);
     const [constraintViolations, setConstraintViolations] = useState([]);

     const activeProductRulesObjects = ProductRuleHelper.getActiveRules();
     const activeForeignShopRules = ForeignShopRuleHelper.getActiveRules();
     const activeActionRules = ActionRuleHelper.getActiveRules();

     const resolveChannel = (channel) => {
          if (!channel) return null;
          return channels.find((candidate) =>
               (channel.id != null && String(candidate.id) === String(channel.id)) ||
               candidate.channelName === (channel.channelName || channel.value || channel.label)
          ) || channel;
     };

     const updateChannelContext = (channel, language = null) => {
          const nextChannel = resolveChannel(channel);
          const currentChannel = properties.channel;
          const currentLanguage = LanguageAndChannelHelper.getLanguage();
          const nextLanguage = language || currentLanguage;
          const channelChanged =
               String(currentChannel?.id ?? currentChannel?.channelName ?? "") !==
               String(nextChannel?.id ?? nextChannel?.channelName ?? "");
          const languageChanged =
               String(currentLanguage?.value ?? currentLanguage?.label ?? currentLanguage ?? "") !==
               String(nextLanguage?.value ?? nextLanguage?.label ?? nextLanguage ?? "");

          if (languageChanged) {
               LanguageAndChannelHelper.setLanguage(nextLanguage);
          }
          if (channelChanged || !currentChannel) {
               LanguageAndChannelHelper.setChannel({
                    ...nextChannel,
                    label: nextChannel?.channelName || nextChannel?.label || nextChannel?.value,
                    value: nextChannel?.channelName || nextChannel?.value || nextChannel?.label
               });
          }
          LanguageAndChannelHelper.toggleLanguageAndChannel(true);

          if (!channelChanged && !languageChanged) {
               return;
          }

          if (channelChanged) {
               if (nextChannel?.id != null) {
                    setStoredChannelId(customerId, nextChannel.id);
               }
               setRuleGroups([]);
               setProperties((prev) => ({
                    ...prev,
                    channel: nextChannel,
                    group: null,
                    priority: 0
               }));
               actionRuleHelpers.CalculationChannelAndRuleHelper.toggle(false);
          }

          foreignShopRuleHelpers.PriceTrackingHelper.setActive(false);
          foreignShopRuleHelpers.PriceTrackingHelper.setShopKeysWithPrio({});
          foreignShopRuleHelpers.UseAllPricesFromGroupHelper.setActive(false);
          foreignShopRuleHelpers.UseAllPricesFromGroupHelper.setGroupName("");
     };

     const loadRuleGroupsForChannel = async (channelId, backupMode = false) => {
          const groups = await RuleEngineBackend.getRuleGroups(token, customerId, channelId);
          if (!groups) return [];
          if (backupMode) {
               return groups.filter((group) => group.groupName === BACKUP_RULE_GROUP_NAME);
          }
          return groups.filter((group) => group.groupName !== BACKUP_RULE_GROUP_NAME);
     };

     const applySplitProperties = async (splitProperties, normalizedChannels) => {
          if (splitProperties.backupRule?.id) {
               const backupRules = await RuleEngineBackend.getBackupRules(token, customerId);
               const matchingBackupRule = backupRules?.find((rule) => rule.id === splitProperties.backupRule.id);
               if (matchingBackupRule) {
                    splitProperties.backupRule = matchingBackupRule;
               }
          }

          const loadedChannel = splitProperties.channel || splitProperties.group?.channel || null;
          if (loadedChannel) {
               const synchronizedChannel = normalizedChannels?.find((channel) =>
                    String(channel.id) === String(loadedChannel.id) ||
                    channel.channelName === loadedChannel.channelName
               ) || loadedChannel;
               splitProperties.channel = synchronizedChannel;
               LanguageAndChannelHelper.setChannel({
                    ...synchronizedChannel,
                    label: synchronizedChannel.channelName,
                    value: synchronizedChannel.channelName
               });
               LanguageAndChannelHelper.toggleLanguageAndChannel(true);
          }
          setProperties(splitProperties);

          const channelId = splitProperties.channel?.id ?? splitProperties.group?.channel?.id;
          if (PPUtils.isPositiveIntId(channelId)) {
               setStoredChannelId(customerId, channelId);
               const groups = await loadRuleGroupsForChannel(
                    channelId,
                    splitProperties.isBackupRule || false
               );
               if (groups) {
                    setRuleGroups(groups);
               }
          }
     };

     const loadRuleFromTemplate = async (templateId, normalizedChannels) => {
          const templateRule = await RuleEngineBackend.getRuleById(token, templateId);
          if (!templateRule) {
               toast.error("Vorlagen-Regel konnte nicht gefunden werden");
               return;
          }

          const splitProperties = RuleStateService.splitAsTemplate(
               templateRule,
               productRuleHelpers,
               foreignShopRuleHelpers,
               actionRuleHelpers
          );

          await applySplitProperties(splitProperties, normalizedChannels);
          setTemplateRuleInfo({ id: templateRule.id, name: templateRule.name || `Regel ${templateRule.id}` });
     };

     const handleSelectTemplateRule = async (templateRule) => {
          setTemplateModalVisible(false);
          if (!templateRule?.id) {
               if (templateRuleIdParam) {
                    navigate("/pricemanagement/editor", { replace: true });
                    setTemplateRuleInfo(null);
               }
               return;
          }

          setIsLoading(true);
          try {
               let normalizedChannels = channels;
               if (!normalizedChannels.length) {
                    const channelList = await RuleEngineBackend.getChannels(customerId, token);
                    normalizedChannels = channelList
                         ? (Array.isArray(channelList) ? channelList : [channelList]).filter((ch) => !ch.hidden)
                         : [];
                    setChannels(normalizedChannels);
               }
               await loadRuleFromTemplate(templateRule.id, normalizedChannels);
               navigate(`/pricemanagement/editor?templateRuleId=${templateRule.id}`, { replace: true });
          } catch (error) {
               console.error("Error loading template rule:", error);
               toast.error("Fehler beim Laden der Vorlage");
          } finally {
               setIsLoading(false);
          }
     };

     useEffect(() => {
          if (!isBackupRuleMode || ruleId) return;
          if (!PriceChangeMarginHelper.isActive()) {
               PriceChangeMarginHelper.toggle(true);
               PriceChangeMarginHelper.setMargeType("NETTOFAKTOR");
               PriceChangeMarginHelper.setNettoBruttoValue(1.5);
               PriceChangeMarginHelper.setMargeFall("ALL_PRODUCTS");
               PriceChangeMarginHelper.setMinimumPriceFormulaBase("Einkaufspreis");
               PriceChangeMarginHelper.setMinimumPriceFormulaAddTax(true);
          }
          setProperties((prev) => ({
               ...prev,
               isBackupRule: true,
               priority: 0
          }));
     }, [isBackupRuleMode, ruleId]);

     useEffect(() => {
          const channelId = properties.channel?.id;
          if (!channelId) return;

          let cancelled = false;
          (async () => {
               const groups = await loadRuleGroupsForChannel(channelId, isBackupRuleMode);
               if (cancelled) return;
               setRuleGroups(groups);
               if (isBackupRuleMode && groups.length > 0) {
                    setProperties((prev) => ({
                         ...prev,
                         group: prev.group?.groupName === BACKUP_RULE_GROUP_NAME ? prev.group : groups[groups.length - 1]
                    }));
               }
          })();

          return () => {
               cancelled = true;
          };
     }, [properties.channel?.id, isBackupRuleMode]);

     useEffect(() => {
          async function fetchData() {
               if (customerId == null) {
                    setIsLoading(false);
                    return;
               }
               setIsLoading(true);
               try {
                    await fetchServerOptions();
                    computeOptions();

                    const channelList = await RuleEngineBackend.getChannels(customerId, token);
                    const normalizedChannels = channelList
                         ? (Array.isArray(channelList) ? channelList : [channelList])
                         : [];
                    if (channelList) {
                         const visibleChannels = normalizedChannels.filter((ch) => !ch.hidden);
                         const channelsToUse = visibleChannels.length > 0 ? visibleChannels : normalizedChannels;
                         setChannels(channelsToUse);

                         if (!ruleId) {
                              const resolvedChannelId = resolveChannelId(channelsToUse, customerId, properties.channel?.id);
                              const defaultChannel = findChannelById(channelsToUse, resolvedChannelId);
                              if (defaultChannel) {
                                   setProperties((prev) => (prev.channel ? prev : { ...prev, channel: defaultChannel }));
                              }
                         }
                    }

                    if (ruleId) {
                         const existingRule = await RuleEngineBackend.getRuleById(token, ruleId);
                         if (existingRule) {
                              const splitProperties = RuleStateService.split(
                                   existingRule,
                                   productRuleHelpers,
                                   foreignShopRuleHelpers,
                                   actionRuleHelpers
                              );
                              await applySplitProperties(splitProperties, normalizedChannels);
                         } else {
                              console.error("Rule not found:", ruleId);
                              toast.error("Regel konnte nicht gefunden werden");
                         }
                    } else if (!isBackupRuleMode && templateRuleIdParam) {
                         await loadRuleFromTemplate(Number(templateRuleIdParam), normalizedChannels);
                    }
               } catch (error) {
                    console.error("Error in fetchData:", error);
                    toast.error("Fehler beim Laden der Daten");
               } finally {
                    setIsLoading(false);
               }
          }
          fetchData();
     }, []);

     const handleCreateGroup = async (groupName) => {
          const channel = properties.channel;
          if (!channel?.id) {
               toast.error("Bitte wählen Sie zuerst einen Kanal aus");
               return;
          }
          const newGroup = await RuleEngineBackend.createRuleGroup(token, customerId, channel, groupName);
          if (newGroup) {
               setRuleGroups(prev => [...prev, newGroup]);
               setProperties(prev => ({
                    ...prev,
                    group: newGroup
               }));
          }
     };

     const handleSelectBackupRule = (backupRule) => {
          setProperties(prev => ({
               ...prev,
               backupRule: backupRule
          }));
     };

     const handleSaveRule = async (activateOnSave = false) => {
          if (isBackupRuleMode) {
               const hasDisallowedProductRules = activeProductRulesObjects.length > 0;
               const hasDisallowedForeignShopRules = activeForeignShopRules.length > 0;
               const hasDisallowedActionRules = activeActionRules.some((rule) => rule.keyString !== "priceChangeMargin");
               const hasMarginRule = activeActionRules.some((rule) => rule.keyString === "priceChangeMargin");

               if (hasDisallowedProductRules || hasDisallowedForeignShopRules || hasDisallowedActionRules || !hasMarginRule) {
                    setConstraintViolations([{
                         id: "BACKUP_RULE_COMPONENTS_INVALID",
                         level: "error",
                         scope: "global",
                         blocking: true,
                         message: "Backup-Regeln duerfen nur die Komponente \"Preisanpassung nach Marge\" enthalten."
                    }]);
                    setConstraintModalVisible(true);
                    return;
               }
          }

          const context = RuleConstraintService.collectContext({
               properties,
               productRuleHelpers,
               foreignShopRuleHelpers,
               actionRuleHelpers
          });

          const { blockingErrors } = RuleConstraintService.validateBeforeSave(context);

          if (blockingErrors.length > 0) {
               setConstraintViolations(blockingErrors);
               setConstraintModalVisible(true);
               return;
          }

          setIsSaving(true);
          try {
               const savedRule = await RuleStateService.merge(
                    {
                         ...properties,
                         userId,
                         customerId,
                         isBackupRule: isBackupRuleMode || properties.isBackupRule || false,
                         activateOnSave: Boolean(activateOnSave)
                    },
                    ProductRuleHelper,
                    ForeignShopRuleHelper,
                    ActionRuleHelper
               );
               if (activateOnSave) {
                    const savedRuleId = savedRule?.id || properties.id || ruleId;
                    if (savedRuleId) {
                         await RuleEngineBackend.activateRule(userId, token, savedRuleId);
                    }
               }
               toast.success(activateOnSave ? "Regel aktiviert und gespeichert" : "Regel gespeichert");
               if (!ruleId && savedRule?.id) {
                    navigate(`/pricemanagement/editor/${savedRule.id}`, { replace: true });
               }
          } catch (error) {
               console.error("Error saving rule:", error);
               const errorMessage = error?.message || "Fehler beim Speichern der Regel";
               toast.error(errorMessage);
          } finally {
               setIsSaving(false);
          }
     };

     const handleEditProductRule = (rule) => {
          if (isBackupRuleMode) {
               toast.error("In einer Backup-Regel ist nur Preisanpassung nach Marge erlaubt");
               return;
          }
          setEditingRule(rule);
          setProductRuleModalVisible(true);
     };

     const handleEditForeignShopRule = (rule) => {
          if (isBackupRuleMode) {
               toast.error("In einer Backup-Regel ist nur Preisanpassung nach Marge erlaubt");
               return;
          }
          setEditingRule(rule);
          setForeignShopRuleModalVisible(true);
     };

     const handleEditActionRule = (rule) => {
          setEditingRule(rule);
          setActionRuleModalVisible(true);
     };

     const handleDeleteProductRule = (rule) => {
          if (!rule) return;

          if (rule.keyString === 'stock') {
               const { StockHelper } = productRuleHelpers;
               const { ForeignShopStockHelper } = foreignShopRuleHelpers;

               const foreignShopRules = ForeignShopStockHelper.getForeignShopStockRules();
               const affectedRules = foreignShopRules.filter(r => r.stockRuleReference === rule.id);
               const allStockRules = StockHelper.getStockRules();
               const willBeLastStockRule = allStockRules.length === 1 && allStockRules[0].id === rule.id;

               let additionalMessage = null;
               if (affectedRules.length > 0) {
                    additionalMessage = `Das Löschen dieser Verfügbarkeits-Regel führt zur automatischen Löschung von ${affectedRules.length} verknüpften Fremdshop-Verfügbarkeits-Regel(n).`;
               }
               if (willBeLastStockRule && foreignShopRules.length > affectedRules.length) {
                    const remainingRules = foreignShopRules.filter(r => r.stockRuleReference !== rule.id);
                    if (remainingRules.length > 0) {
                         additionalMessage = `Da dies die letzte Verfügbarkeits-Regel ist, werden auch alle anderen ${remainingRules.length} Fremdshop-Verfügbarkeits-Regel(n) gelöscht.`;
                    }
               }

               setDeleteModalConfig({
                    title: "Verfügbarkeits-Regel löschen",
                    message: "Sind Sie sicher, dass Sie diese Verfügbarkeits-Regel löschen möchten?",
                    additionalMessage
               });
          } else {
               setDeleteModalConfig({
                    title: "Regel löschen",
                    message: `Sind Sie sicher, dass Sie diese ${rule.name || 'Regel'} löschen möchten?`,
                    additionalMessage: null
               });
          }

          setRuleToDelete({ rule, type: 'product' });
          setDeleteModalVisible(true);
     };

     const handleDeleteForeignShopRule = (rule) => {
          if (!rule) return;

          const foreignShopRuleLabels = {
               foreignShopstock: 'Fremdshop-Verfügbarkeits-Regel',
               priceTracking: 'Preistracking-Regel',
               useAllPricesFromGroup: 'Produktgruppen-Preis-Regel'
          };

          let additionalMessage = null;
          if (rule.keyString === 'foreignShopstock') {
               const { PriceChangeHelper } = actionRuleHelpers;
               const linkedPriceChanges = PriceChangeHelper.getPriceAdjustmentRules()
                    .filter(pa => pa.foreignShopStockRuleReference === rule.id);
               if (linkedPriceChanges.length > 0) {
                    additionalMessage = `Das Löschen dieser Fremdshop-Verfügbarkeits-Regel führt zur automatischen Löschung von ${linkedPriceChanges.length} verknüpften Preisangleichungs-Regel(n).`;
               }
          }

          setDeleteModalConfig({
               title: "Fremdshop-Regel löschen",
               message: `Sind Sie sicher, dass Sie diese ${foreignShopRuleLabels[rule.keyString] || 'Fremdshop-Regel'} löschen möchten?`,
               additionalMessage
          });

          setRuleToDelete({ rule, type: 'foreignShop' });
          setDeleteModalVisible(true);
     };

     const handleDeleteActionRule = (rule) => {
          if (!rule) return;

          setDeleteModalConfig({
               title: "Aktions-Regel löschen",
               message: `Sind Sie sicher, dass Sie diese ${rule.name || 'Aktions-Regel'} löschen möchten?`,
               additionalMessage: null
          });

          setRuleToDelete({ rule, type: 'action' });
          setDeleteModalVisible(true);
     };

     const executeDelete = () => {
          if (!ruleToDelete) return;

          const { rule, type } = ruleToDelete;

          try {
               if (type === 'product') {
                    const { StockHelper, LanguageAndChannelHelper, ProductAttributeHelper, RankingAndFilterHelper, ProductCategoryHelper } = productRuleHelpers;
                    const { ForeignShopStockHelper } = foreignShopRuleHelpers;

                    if (rule.keyString === 'stock') {
                         const { PriceChangeHelper } = actionRuleHelpers;
                         const foreignShopRules = ForeignShopStockHelper.getForeignShopStockRules();
                         const affectedRules = foreignShopRules.filter(r => r.stockRuleReference === rule.id);
                         const allStockRules = StockHelper.getStockRules();
                         const willBeLastStockRule = allStockRules.length === 1 && allStockRules[0].id === rule.id;

                     const removeLinkedPriceAdjustments = (foreignStockId) => {
                          if (foreignStockId == null) return;
                          PriceChangeHelper.getPriceAdjustmentRules()
                               .filter(pa => pa.foreignShopStockRuleReference === foreignStockId)
                               .forEach(pa => PriceChangeHelper.removePriceAdjustmentRule(pa.id));
                     };
                         affectedRules.forEach(r => removeLinkedPriceAdjustments(r.id));

                         affectedRules.forEach(r => {
                              ForeignShopStockHelper.removeForeignShopStockRule(r.id);
                         });

                         if (willBeLastStockRule && foreignShopRules.length > affectedRules.length) {
                              const remainingRules = foreignShopRules.filter(r => r.stockRuleReference !== rule.id);
                              remainingRules.forEach(r => {
                                   removeLinkedPriceAdjustments(r.id);
                                   ForeignShopStockHelper.removeForeignShopStockRule(r.id);
                              });
                         }

                         StockHelper.removeStockRule(rule.id);
                    } else if (rule.keyString === 'languageAndChannel') {
                         LanguageAndChannelHelper.toggleLanguageAndChannel(false);
                    } else if (rule.keyString === 'product-attribute') {
                         ProductAttributeHelper.clear?.();
                    } else if (rule.keyString === 'rankingAndFilter') {
                         RankingAndFilterHelper.clear?.();
                    } else if (rule.keyString === 'productCategory') {
                         ProductCategoryHelper.clear?.();
                    }
               } else if (type === 'foreignShop') {
                    const { ForeignShopStockHelper, PriceTrackingHelper, UseAllPricesFromGroupHelper } = foreignShopRuleHelpers;
                    const { PriceChangeHelper } = actionRuleHelpers;

                    if (rule.keyString === 'foreignShopstock') {
                         PriceChangeHelper.getPriceAdjustmentRules()
                              .filter(pa => pa.foreignShopStockRuleReference === rule.id)
                              .forEach(pa => PriceChangeHelper.removePriceAdjustmentRule(pa.id));
                         ForeignShopStockHelper.removeForeignShopStockRule(rule.id);
                    } else if (rule.keyString === 'priceTracking') {
                         PriceTrackingHelper.togglePriceTracking(false);
                    } else if (rule.keyString === 'useAllPricesFromGroup') {
                         UseAllPricesFromGroupHelper.setGroupName("");
                         UseAllPricesFromGroupHelper.setActive(false);
                    }
               } else if (type === 'action') {
                    const { PriceChangeMarginHelper, CalculationChannelAndRuleHelper, RoundPriceHelper, ChangeIntervalHelper, PriceChangeHelper, MaxPriceChangeHelper } = actionRuleHelpers;

                    if (rule.keyString === 'priceChangeMargin') {
                         PriceChangeMarginHelper.clear?.();
                    } else if (rule.keyString === 'calculationChannelAndRulePrice') {
                         CalculationChannelAndRuleHelper.toggle(false);
                    } else if (rule.keyString === 'roundPrice') {
                         RoundPriceHelper.toggle(false);
                    } else if (rule.keyString === 'changeInterval') {
                         ChangeIntervalHelper.toggle(false);
                    } else if (rule.keyString === 'priceChange') {
                         PriceChangeHelper.removePriceAdjustmentRule(rule.id);
                    } else if (rule.keyString === 'maxPriceAdjustment') {
                         MaxPriceChangeHelper.toggle(false);
                    } else if (rule.keyString === 'amazonBuyBox') {
                         actionRuleHelpers.AmazonBuyBoxHelper.toggle(false);
                    }
               }

          } catch (error) {
               console.error("Error deleting rule:", error);
               toast.error("Fehler beim Löschen der Regel");
          } finally {
               setDeleteModalVisible(false);
               setRuleToDelete(null);
          }
     };

     if (customerId == null) {
          return (
               <div className="container-fluid priceman-page">
                    <div className="card shadow">
                         <div className="card-body">
                              <PPMatchingStatusBanner variant="no_customer" />
                         </div>
                    </div>
               </div>
          )
     }

     return (
          <>
               {isLoading && (
                    <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                         style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
                         <div className="text-center text-white">
                              <output className="spinner-border mb-3">
                                   <span className="visually-hidden">Loading...</span>
                              </output>
                              <div>Regel wird geladen...</div>
                         </div>
                    </div>
               )}
               <div className="container-fluid">
                    <div className="row">
                         <div className="col-12">
                              <div className="card shadow">
                                   <div className="card-body">

                                        <h4 className="mb-1">Eigenschaften</h4>
                                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
                                             <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
                                                  {ruleId ? `Regel bearbeiten (ID: ${ruleId})` : "Neue Regel erstellen"}
                                                  {templateRuleInfo && (
                                                       <span className="ms-2 badge bg-light text-dark border">
                                                            Vorlage: {templateRuleInfo.name} (ID: {templateRuleInfo.id})
                                                       </span>
                                                  )}
                                             </p>
                                             {!ruleId && !isBackupRuleMode && (
                                                  <button
                                                       type="button"
                                                       className="btn btn-outline-primary btn-sm"
                                                       onClick={() => setTemplateModalVisible(true)}
                                                  >
                                                       <i className="fas fa-copy me-1"></i>{' '}Regel aus Vorlage erstellen
                                                  </button>
                                             )}
                                        </div>
                                        {isBackupRuleMode && (
                                             <div className="alert alert-warning">
                                                  Backup-Regel Modus: Nur &quot;Preisanpassung nach Marge&quot; ist erlaubt.
                                             </div>
                                        )}

                                        <div className="row mb-5">
                                             <div className="col-12 col-md-3 mb-3">
                                                  <div className="card h-100">
                                                       <div className="card-body">
                                                            <div className="mb-3 mb-3">
                                                                 <label htmlFor="property_name_input" className="form-label fw-semibold">Name</label>
                                                                 <input
                                                                      type="text"
                                                                      value={properties.name}
                                                                      className="form-control"
                                                                      onChange={(e) => {
                                                                           setProperties(prev => ({
                                                                                ...prev,
                                                                                name: e.target.value
                                                                           }));
                                                                      }}
                                                                      placeholder="Name eingeben"
                                                                      id="property_name_input"
                                                                 />
                                                            </div>
                                                            <div className="mb-3 mb-0">
                                                                 <label htmlFor="property_description_area" className="form-label fw-semibold">Beschreibung</label>
                                                                 <textarea
                                                                      style={{ resize: 'none' }}
                                                                      id="property_description_area"
                                                                      rows="4"
                                                                      className="form-control"
                                                                      value={properties.description}
                                                                      placeholder="Beschreibung eingeben"
                                                                      onChange={(e) => {
                                                                           setProperties(prev => ({
                                                                                ...prev,
                                                                                description: e.target.value
                                                                           }));
                                                                      }}
                                                                 />
                                                            </div>
                                                       </div>
                                                  </div>
                                             </div>

                                             <div className="col-12 col-md-4 mb-3">
                                                  <div className="card h-100">
                                                       <div className="card-body d-flex flex-column">
                                                            <div className="mb-3 mb-3">
                                                                 <label htmlFor="property_channel_select" className="form-label fw-semibold">Kanal</label>
                                                                 <Select
                                                                      inputId="property_channel_select"
                                                                      className="priceman-channel-select"
                                                                      options={channels.map((channel) => ({
                                                                           label: channel.channelName,
                                                                           value: channel
                                                                      }))}
                                                                      value={properties.channel ? {
                                                                           label: properties.channel.channelName,
                                                                           value: properties.channel
                                                                      } : null}
                                                                      onChange={(selected) => {
                                                                           if (selected == null) {
                                                                                return
                                                                           }
                                                                           updateChannelContext(selected.value);
                                                                      }}
                                                                      placeholder="Kanal wählen"
                                                                      isClearable={false}
                                                                 />
                                                            </div>
                                                            <div className="mb-3 mb-3">
                                                                 <label htmlFor="prio_select" className="form-label fw-semibold">Priorität</label>
                                                                 <button
                                                                      className="btn btn-outline-primary w-100 text-start d-flex justify-content-between align-items-center"
                                                                      onClick={() => setPriorityModalVisible(true)}
                                                                      type="button"
                                                                      disabled={!properties.channel}
                                                                 >
                                                                      <span>
                                                                           {properties.priority ? `Priorität: ${properties.priority}` : 'Priorität wählen'}
                                                                      </span>
                                                                      <i className="fas fa-sort"></i>
                                                                 </button>
                                                                 {!properties.channel && (
                                                                      <small className="text-muted d-block mt-1">
                                                                           Bitte wählen Sie zuerst einen Kanal aus
                                                                      </small>
                                                                 )}
                                                            </div>
                                                            <div className="mb-3 mb-3">
                                                                 <label htmlFor="property_group_select" className="form-label fw-semibold">Gruppe</label>
                                                                 <div className="input-group">
                                                                      <Select
                                                                           inputId="property_group_select"
                                                                           className="flex-fill"
                                                                           options={ruleGroups.map(group => ({
                                                                                label: group.groupName,
                                                                                value: group
                                                                           }))}
                                                                           name="selectgroup"
                                                                           value={properties.group ? {
                                                                                label: properties.group.groupName,
                                                                                value: properties.group
                                                                           } : null}
                                                                           onChange={(selectedOption) => {
                                                                                if (selectedOption == null) {
                                                                                     return
                                                                                }
                                                                                const nextGroup = selectedOption.value
                                                                                const nextGroupChannel = nextGroup?.channel
                                                                                if (properties.channel == null && nextGroupChannel != null) {
                                                                                     updateChannelContext(nextGroupChannel);
                                                                                }
                                                                                setProperties(prev => {
                                                                                     const keepExistingChannel = prev.channel != null
                                                                                     const channelFromGroup = keepExistingChannel ? prev.channel : nextGroupChannel
                                                                                     return {
                                                                                          ...prev,
                                                                                          group: nextGroup,
                                                                                          channel: channelFromGroup ?? null
                                                                                     }
                                                                                });
                                                                           }}
                                                                           placeholder="Gruppe wählen"
                                                                      />
                                                                      <button
                                                                           className="btn btn-outline-primary"
                                                                           type="button"
                                                                           title="Neue Gruppe hinzufügen"
                                                                           onClick={() => setAddGroupModalVisible(true)}
                                                                      >
                                                                           <i className="fas fa-plus"></i>
                                                                      </button>
                                                                 </div>
                                                            </div>
                                                            <div className="mt-auto">
                                                                 {properties.backupRule ? (
                                                                      <div className="mb-2">
                                                                           <div className="card border-success">
                                                                                <div className="card-body p-2">
                                                                                     <div className="d-flex align-items-center justify-content-between">
                                                                                          <div>
                                                                                               <small className="text-success fw-bold">
                                                                                                    <i className="fas fa-shield-alt me-1"></i>
                                                                                                    {properties.backupRule.name}
                                                                                               </small>
                                                                                               <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                                                                    {properties.backupRule.description}
                                                                                               </div>
                                                                                          </div>
                                                                                          <button
                                                                                               className="btn btn-sm btn-outline-danger"
                                                                                               type="button"
                                                                                               title="Backup-Regel entfernen"
                                                                                               onClick={() => setProperties(prev => ({ ...prev, backupRule: null }))}
                                                                                          >
                                                                                               <i className="fas fa-times"></i>
                                                                                          </button>
                                                                                     </div>
                                                                                </div>
                                                                           </div>
                                                                      </div>
                                                                 ) : null}
                                                                 <button
                                                                      className="btn btn-outline-secondary w-100"
                                                                      type="button"
                                                                      onClick={() => setBackupRuleModalVisible(true)}
                                                                 >
                                                                      <i className="fas fa-shield-alt me-2"></i>{' '}Backup Regel hinzufügen
                                                                 </button>
                                                            </div>
                                                       </div>
                                                  </div>
                                             </div>

                                             <div className="col-12 col-md-2 mb-3">
                                                  <div className="card h-100">
                                                       <div className="card-body">
                                                            <div className="form-check mb-3">
                                                                 <input
                                                                      className="form-check-input"
                                                                      type="checkbox"
                                                                      id="factfinder"
                                                                      checked={properties.factfinder}
                                                                      onChange={(e) => {
                                                                           setProperties(prev => ({
                                                                                ...prev,
                                                                                factfinder: e.target.checked
                                                                           }));
                                                                      }}
                                                                 />
                                                                 <label className="form-check-label fw-semibold" htmlFor="factfinder">Factfinder</label>
                                                            </div>
                                                            <div className="form-check mb-3">
                                                                 <input
                                                                      className="form-check-input"
                                                                      type="checkbox"
                                                                      id="specialRule"
                                                                      checked={properties.sepcialRule}
                                                                      onChange={(e) => {
                                                                           setProperties(prev => ({
                                                                                ...prev,
                                                                                sepcialRule: e.target.checked
                                                                           }));
                                                                      }}
                                                                 />
                                                                 <label className="form-check-label fw-semibold" htmlFor="specialRule">Sonderregeln</label>
                                                            </div>
                                                            <div className="form-check mb-3">
                                                                 <input
                                                                      className="form-check-input"
                                                                      type="checkbox"
                                                                      id="triggerRule"
                                                                      checked={properties.triggerRule}
                                                                      onChange={(e) => {
                                                                           setProperties(prev => ({
                                                                                ...prev,
                                                                                triggerRule: e.target.checked
                                                                           }));
                                                                      }}
                                                                 />
                                                                 <label className="form-check-label fw-semibold" htmlFor="triggerRule">Regel anstoßen zulassen</label>
                                                            </div>
                                                            <div className="form-check mb-3">
                                                                 <input
                                                                      className="form-check-input"
                                                                      type="checkbox"
                                                                      id="shippingCost"
                                                                      checked={properties.shippingCost}
                                                                      onChange={(e) => {
                                                                           setProperties(prev => ({
                                                                                ...prev,
                                                                                shippingCost: e.target.checked
                                                                           }));
                                                                      }}
                                                                 />
                                                                 <label className="form-check-label fw-semibold" htmlFor="shippingCost">Versandkosten berücksichtigen</label>
                                                            </div>
                                                       </div>
                                                  </div>
                                             </div>

                                             <div className="col-12 col-md-3 mb-3">
                                                  <div className="card h-100 border-success">
                                                       <div className="card-body d-flex flex-column justify-content-center gap-2">
                                                            <button
                                                                 className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                                                                 onClick={() => {
                                                                      setTestRuleModalVisible(true);
                                                                 }}
                                                            >
                                                                 <i className="fas fa-flask me-2"></i>{' '}Regel testen
                                                            </button>
                                                            {ruleId && (
                                                                 <button
                                                                      className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                                                                      onClick={() => {
                                                                           setHistoryModalVisible(true);
                                                                      }}
                                                                 >
                                                                      <i className="fas fa-history me-2"></i>{' '}Verlauf
                                                                 </button>
                                                            )}
                                                            <button
                                                                 className="btn btn-outline-success d-flex align-items-center justify-content-center"
                                                                 onClick={() => handleSaveRule(false)}
                                                                 disabled={isSaving || isLoading}
                                                            >
                                                                 {isSaving ? (
                                                                      <>
                                                                           <output className="spinner-border spinner-border-sm me-2">
                                                                                <span className="visually-hidden">Loading...</span>
                                                                           </output>
                                                                           Speichert...
                                                                      </>
                                                                 ) : (
                                                                      <>
                                                                           <i className="fas fa-save me-2"></i>{' '}Speichern
                                                                      </>
                                                                 )}
                                                            </button>
                                                            <button
                                                                 className="btn btn-success d-flex align-items-center justify-content-center"
                                                                 onClick={() => handleSaveRule(true)}
                                                                 disabled={isSaving || isLoading}
                                                            >
                                                                 {isSaving ? (
                                                                      <>
                                                                           <output className="spinner-border spinner-border-sm me-2">
                                                                                <span className="visually-hidden">Loading...</span>
                                                                           </output>
                                                                           Speichert...
                                                                      </>
                                                                 ) : (
                                                                      <>
                                                                           <i className="fas fa-power-off me-2"></i>{' '}Aktivieren & Speichern
                                                                      </>
                                                                 )}
                                                            </button>
                                                       </div>
                                                  </div>
                                             </div>
                                        </div>

                                        <h4 className="mb-4">Regel-Konfiguration</h4>

                                        <ArcherContainer
                                             endShape={{
                                                  arrow: {
                                                       arrowLength: 0,
                                                       arrowThickness: 0
                                                  }
                                             }}
                                             style={{
                                                  position: 'relative',
                                                  zIndex: 1000
                                             }}
                                             svgContainerStyle={{
                                                  position: 'absolute',
                                                  top: 0,
                                                  left: 0,
                                                  width: '100%',
                                                  height: '100%',
                                                  pointerEvents: 'none',
                                                  zIndex: 9999
                                             }}
                                        >
                                             <div className="row">
                                                  <div className="col-12 col-md-4 mb-4">
                                                       <div className="card h-100 pp-editor-column-card" style={{ backgroundColor: '#f8fdf8' }}>
                                                            <div className="card-header bg-light">
                                                                 <h5 className="card-title mb-0 text-center fw-bold">Eigene Produkte</h5>
                                                            </div>
                                                            <div className="card-body d-flex flex-column">
                                                                 <div className="flex-grow-1 mb-3">
                                                                      {
                                                                           activeProductRulesObjects && activeProductRulesObjects.length > 0 &&
                                                                           activeProductRulesObjects.map((obj, i) => <ProductRuleCard obj={obj} key={obj.id ?? `product-rule-${i}`} index={i} onEditRule={handleEditProductRule} onDeleteRule={handleDeleteProductRule} />)
                                                                      }
                                                                 </div>
                                                                 <button
                                                                      type="button"
                                                                      className="border border-2 border-dashed border-success rounded p-3 text-center text-success pp-clickable-block"
                                                                     style={{ cursor: isBackupRuleMode ? 'not-allowed' : 'pointer', minHeight: '80px', opacity: isBackupRuleMode ? 0.6 : 1 }}
                                                                      onClick={() => {
                                                                          if (isBackupRuleMode) return;
                                                                           setEditingRule(null);
                                                                           setProductRuleModalVisible(true);
                                                                      }}
                                                                 >
                                                                      <i className="fas fa-plus fs-4 mb-2"></i>
                                                                      <span className="fw-semibold d-block">Regel hinzufügen</span>
                                                                 </button>
                                                            </div>
                                                       </div>
                                                  </div>

                                                  <div className="col-12 col-md-4 mb-4">
                                                       <div className="card h-100 pp-editor-column-card" style={{ backgroundColor: '#f0f8ff' }}>
                                                            <div className="card-header bg-light">
                                                                 <h5 className="card-title mb-0 text-center fw-bold">Fremdshops</h5>
                                                            </div>
                                                            <div className="card-body d-flex flex-column">
                                                                 <div className="flex-grow-1 mb-3">
                                                                      {
                                                                           activeForeignShopRules && activeForeignShopRules.length > 0 &&
                                                                           activeForeignShopRules.map((obj, i) => <ForeignShopRuleCard obj={obj} key={obj.id ?? `foreign-rule-${i}`} index={i} onEditRule={handleEditForeignShopRule} onDeleteRule={handleDeleteForeignShopRule} />)
                                                                      }
                                                                 </div>
                                                                 <button
                                                                      type="button"
                                                                      className="border border-2 border-dashed border-info rounded p-3 text-center text-info pp-clickable-block"
                                                                     style={{ cursor: isBackupRuleMode ? 'not-allowed' : 'pointer', minHeight: '80px', opacity: isBackupRuleMode ? 0.6 : 1 }}
                                                                      onClick={() => {
                                                                          if (isBackupRuleMode) return;
                                                                           setEditingRule(null);
                                                                           setForeignShopRuleModalVisible(true);
                                                                      }}
                                                                 >
                                                                      <i className="fas fa-plus fs-4 mb-2"></i>
                                                                      <span className="fw-semibold d-block">Regel hinzufügen</span>
                                                                 </button>
                                                            </div>
                                                       </div>
                                                  </div>

                                                  <div className="col-12 col-md-4 mb-4">
                                                       <div className="card h-100 pp-editor-column-card" style={{ backgroundColor: '#fff5f5' }}>
                                                            <div className="card-header bg-light">
                                                                 <h5 className="card-title mb-0 text-center fw-bold">Aktion</h5>
                                                            </div>
                                                            <div className="card-body d-flex flex-column">
                                                                 <div className="flex-grow-1 mb-3">
                                                                      {
                                                                           activeActionRules && activeActionRules.length > 0 &&
                                                                           activeActionRules.map((obj, i) => <ActionRuleCard obj={obj} key={obj.id ?? `action-rule-${i}`} index={i} onEditRule={handleEditActionRule} onDeleteRule={handleDeleteActionRule} />)

                                                                      }
                                                                 </div>
                                                                 <button
                                                                      type="button"
                                                                      className="border border-2 border-dashed border-danger rounded p-3 text-center text-danger pp-clickable-block"
                                                                      style={{ cursor: 'pointer', minHeight: '80px' }}
                                                                      onClick={() => {
                                                                           setEditingRule(null);
                                                                           setActionRuleModalVisible(true);
                                                                      }}
                                                                 >
                                                                      <i className="fas fa-plus fs-4 mb-2"></i>
                                                                      <span className="fw-semibold d-block">Regel hinzufügen</span>
                                                                 </button>
                                                            </div>
                                                       </div>
                                                  </div>
                                             </div>
                                        </ArcherContainer>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>

               <PPPriceManagementProductRuleModal
                    visible={productRuleModalVisible}
                    close={() => {
                         setProductRuleModalVisible(false);
                         setEditingRule(null);
                    }}
                    editingRule={editingRule}
                    onChannelContextChange={({ channel, language }) => updateChannelContext(channel, language)}
               />

               <PPPriceManagementForeignShopRuleModal
                    visible={foreignShopRuleModalVisible}
                    close={() => {
                         setForeignShopRuleModalVisible(false);
                         setEditingRule(null);
                    }}
                    editingRule={editingRule}
               />

               <PPPriceManagementActionRuleModal
                    visible={actionRuleModalVisible}
                    close={() => {
                         setActionRuleModalVisible(false);
                         setEditingRule(null);
                    }}
                    editingRule={editingRule}
                    marginOnly={isBackupRuleMode}
               />

               <PPAddRuleGroupModal
                    visible={addGroupModalVisible}
                    close={() => setAddGroupModalVisible(false)}
                    onSave={handleCreateGroup}
               />

               <PPBackupRuleSelectionModal
                    visible={backupRuleModalVisible}
                    close={() => setBackupRuleModalVisible(false)}
                    onSelect={handleSelectBackupRule}
               />

               <PPRuleTemplateSelectionModal
                    visible={templateModalVisible}
                    close={() => setTemplateModalVisible(false)}
                    onSelect={handleSelectTemplateRule}
               />

               <PPPriceManagementTestRuleModal
                    visible={testRuleModalVisible}
                    close={() => setTestRuleModalVisible(false)}
                    properties={properties}
                    productRuleHelpers={productRuleHelpers}
                    foreignShopRuleHelpers={foreignShopRuleHelpers}
                    actionRuleHelpers={actionRuleHelpers}
               />

               <PPPriceManagementRuleHistoryModal
                    visible={historyModalVisible}
                    close={() => setHistoryModalVisible(false)}
                    ruleId={ruleId}
               />

               <PPPrioritySelectionModal
                    visible={priorityModalVisible}
                    close={() => setPriorityModalVisible(false)}
                    onSelect={(priority) => {
                         setProperties(prev => ({
                              ...prev,
                              priority: Number(priority) || 1
                         }));
                         setPriorityModalVisible(false);
                    }}
                    currentRule={ruleId ? {
                         id: ruleId,
                         name: properties.name || 'Unbenannte Regel',
                         description: properties.description
                    } : null}
                    isNewRule={!ruleId}
                    channel={properties.channel || properties.group?.channel || null}
               />

               <PPDeleteConfirmationModal
                    visible={deleteModalVisible}
                    onClose={() => {
                         setDeleteModalVisible(false);
                         setRuleToDelete(null);
                    }}
                    onConfirm={executeDelete}
                    title={deleteModalConfig.title}
                    message={deleteModalConfig.message}
                    additionalMessage={deleteModalConfig.additionalMessage}
               />

               <PPConstraintViolationModal
                    visible={constraintModalVisible}
                    onClose={() => {
                         setConstraintModalVisible(false);
                         setConstraintViolations([]);
                    }}
                    violations={constraintViolations}
               />
          </>
     );
}
